<template>
  <!-- Dropdown popup positioned relative to filter button -->
  <div v-if="visible" class="absolute right-0 z-50 p-3 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg filter-popup top-full" data-filter-modal>
    <!-- Header -->
    <div class="flex items-center justify-between mb-3">
      <h3 class="text-lg font-normal text-gray-500">Filter</h3>
      <button
        @click="onCancel"
        class="text-gray-400 hover:text-gray-600 focus:outline-none"
      >
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
        </svg>
      </button>
    </div>

    <!-- Filter content -->
    <div class="">
      <div class="grid grid-cols-1 gap-6">
        <!-- Renewal Status -->
        <div>
          <FormLabel htmlFor="renewalStatus">Renewal Status</FormLabel>
          <SelectRadio
            v-model="internalFilters.status"
            :options="statusOptions"
            :defaultText="'Select Renewal Status'"
            class="w-full"
          />
        </div>
      </div>

      <!-- Action buttons -->
      <div class="flex justify-start gap-3 mt-6">
        <Button
          @click="onApply"
          color="primary"
          size="base"
          class="px-6 py-2"
        >
          Filter
        </Button>

        <Button
          @click="onReset"
          color="alternative"
          variant="outlined"
          size="base"
          class="px-6 py-2"
        >
          Reset
        </Button>
      </div>
    </div>
  </div>
</template>

<script>
import FormLabel from '@/components/ui/FormLabel';
import SelectRadio from '@/components/ui/SelectRadio';
import Button from '@/components/ui/Button';

export default {
  name: 'RenewalFilterModal',
  components: {
    FormLabel,
    SelectRadio,
    Button
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    initialFilters: {
      type: Object,
      default: () => ({
        status: null
      })
    }
  },
  data() {
    return {
      internalFilters: {
        status: null
      },
      statusOptions: [
        { value: 'all', text: 'All Type' },
        { value: 'new', text: 'New' },
        { value: 'cancel', text: 'Cancel' },
        { value: 'complete', text: 'Complete' },
        { value: 'error', text: 'Error' }
      ]
    };
  },
  watch: {
    initialFilters: {
      handler(newFilters) {
        this.internalFilters = { ...newFilters };
      },
      deep: true,
      immediate: true
    },
    visible(newVal) {
      if (newVal) {
        // Reset internal filters when modal opens
        this.internalFilters = { ...this.initialFilters };
        // Add click outside listener when modal opens
        this.$nextTick(() => {
          document.addEventListener('click', this.handleClickOutside);
        });
      } else {
        // Remove click outside listener when modal closes
        document.removeEventListener('click', this.handleClickOutside);
      }
    }
  },
  methods: {
    onApply() {
      // Keep the 'all' status for UI state tracking, but send null to API
      const filtersToEmit = {
        ...this.internalFilters,
        // Keep original status for UI tracking
        status: this.internalFilters.status,
        // Send status directly to API (including 'all')
        apiStatus: this.internalFilters.status
      };

      this.$emit('apply-filter', filtersToEmit);
      this.$emit('update:visible', false);
    },
    onReset() {
      this.internalFilters = {
        status: null
      };
      this.$emit('reset-filter');
      this.$emit('update:visible', false);
    },
    onCancel() {
      // Reset to initial filters without applying
      this.internalFilters = { ...this.initialFilters };
      this.$emit('update:visible', false);
    },

    handleClickOutside(event) {
      // Close modal if clicking outside of it
      // Check if clicked element is within the modal or any of its interactive elements
      const isWithinModal = event.target.closest('[data-filter-modal]');
      const isWithinDropdown = event.target.closest('.absolute.right-0.z-10'); // SelectRadio dropdown
      const isButton = event.target.closest('button');
      const isInput = event.target.closest('input');
      const isLabel = event.target.closest('label');

      if (this.visible && !isWithinModal && !isWithinDropdown && !isButton && !isInput && !isLabel) {
        this.$emit('update:visible', false);
      }
    }
  },

  beforeDestroy() {
    // Clean up event listener
    document.removeEventListener('click', this.handleClickOutside);
  }
};
</script>

<style scoped>
/* Filter popup width */
.filter-popup {
  width: 672px;
}
</style>
